import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, User } from '@pbt/pbt-ui-components'

import type { PaymentData } from '~/constants/addPaymentDialogV2Constants'
import { PaymentFlowState } from '~/constants/addPaymentDialogV2Constants'
import type { InvoiceOrEstimate } from '~/types'

import { triggerErrorDoggo } from './errorDoggoUtils'
import { refreshInvoiceAndBalanceHistory } from './paymentDialogUtils'
import { getTerminalInitiationFailureMessage } from './paymentErrorMapping'

interface InitiateTerminalSaleDependencies {
  accountCreditsApplied: boolean
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: Dispatch<any>
  error: any
  initiateTerminalSaleMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string
        invoiceIds: string[]
      }
    }
  }) => Promise<any>
  invoice: InvoiceOrEstimate
  paymentDialogDispatch: React.Dispatch<any>
  setIsButtonActionLoading: (loading: boolean) => void
}

/**
 * Handles the terminal sale mutation with error handling.
 * Extracted to reduce cognitive complexity in useMemo.
 */
export const initiateTerminalSale = async (
  currentPaymentData: PaymentData,
  deps: InitiateTerminalSaleDependencies,
): Promise<void> => {
  const {
    paymentDialogDispatch,
    setIsButtonActionLoading,
    invoice,
    initiateTerminalSaleMutation,
    error,
    accountCreditsApplied,
    dispatch,
    currentUser,
    currentBusiness,
  } = deps
  try {
    const result = await initiateTerminalSaleMutation({
      variables: {
        input: {
          amount: parseFloat(currentPaymentData.amountToCharge || '0'),
          externalDeviceId: currentPaymentData.paymentTerminal || '',
          invoiceIds: invoice.id.split(','),
        },
      },
    })

    if (!result.data?.initiateTerminalSale || error) {
      paymentDialogDispatch({
        type: 'CANCEL_TRANSACTION',
      })
      const errorMessage = getTerminalInitiationFailureMessage(
        accountCreditsApplied,
      )
      const backendErrorMessage = result.errors?.[0]?.message || errorMessage
      triggerErrorDoggo(
        dispatch,
        backendErrorMessage,
        500,
        currentUser,
        currentBusiness,
      )
      paymentDialogDispatch({
        type: 'SET_PAYMENT_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })
    } else {
      paymentDialogDispatch({
        type: 'SET_STATE',
        payload: PaymentFlowState.PAYMENT_REQUEST,
      })
      paymentDialogDispatch({
        type: 'SET_PAYMENT_DATA',
        payload: {
          ...currentPaymentData,
          transactionId: result.data.initiateTerminalSale.id,
          externalDeviceId: result.data.initiateTerminalSale.externalDeviceId,
        },
      })
    }
  } catch (err) {
    paymentDialogDispatch({
      type: 'CANCEL_TRANSACTION',
    })
    const errorMessage = getTerminalInitiationFailureMessage(
      accountCreditsApplied,
    )
    const backendErrorMessage = (err as any)?.message || errorMessage
    triggerErrorDoggo(
      dispatch,
      backendErrorMessage,
      500,
      currentUser,
      currentBusiness,
    )
    paymentDialogDispatch({
      type: 'SET_PAYMENT_ERROR',
      payload: {
        message: errorMessage,
        severity: 'error',
        status: 500,
      },
    })
    if (accountCreditsApplied) {
      refreshInvoiceAndBalanceHistory(invoice, dispatch)
    }
  } finally {
    setIsButtonActionLoading(false)
  }
}
